import { DialogComponent } from '@theme/dialog';

/**
 * Lead-capture welcome popup. Extends the theme's dialog-component with:
 * - auto-open after a configurable delay, once per visitor (localStorage)
 * - immediate open (skipping the delay) right after a successful form submit
 * - a small reopen tab that stays visible once the visitor has dismissed it
 *
 * @extends DialogComponent
 */
export class WelcomePopupComponent extends DialogComponent {
  static #DISMISSED_KEY = 'welcome-popup-dismissed';

  /** @type {number | undefined} */
  #autoOpenTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('dialog:close', this.#handleClose);

    if (this.dataset.forceOpen === 'true') {
      // The page just reloaded after a successful submit: show the result now,
      // don't make the visitor wait through the delay a second time.
      this.showDialog();
      this.#markDismissed();
      return;
    }

    if (WelcomePopupComponent.#isDismissed()) {
      this.#revealReopenTab();
      return;
    }

    const delaySeconds = Number(this.dataset.delaySeconds);
    const delayMs = (Number.isFinite(delaySeconds) && delaySeconds >= 0 ? delaySeconds : 1) * 1000;
    // Count from page start so a late-loading module doesn't push the popup to 30s+.
    const remaining = Math.max(0, delayMs - performance.now());

    this.#autoOpenTimeout = window.setTimeout(() => this.showDialog(), remaining);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearTimeout(this.#autoOpenTimeout);
    this.removeEventListener('dialog:close', this.#handleClose);
  }

  #handleClose = () => {
    window.clearTimeout(this.#autoOpenTimeout);
    this.#markDismissed();
  };

  #markDismissed() {
    WelcomePopupComponent.#setDismissed();
    this.#revealReopenTab();
  }

  #revealReopenTab() {
    this.refs.reopenTab?.removeAttribute('hidden');
  }

  static #isDismissed() {
    return window.localStorage.getItem(WelcomePopupComponent.#DISMISSED_KEY) === '1';
  }

  static #setDismissed() {
    window.localStorage.setItem(WelcomePopupComponent.#DISMISSED_KEY, '1');
  }
}

if (!customElements.get('welcome-popup-component')) {
  customElements.define('welcome-popup-component', WelcomePopupComponent);
}
